import { useEffect, useState } from 'react'
import { CloneConfigurationButton, useOpenAfterClone } from '../components/CloneConfigurationButton'
import {
  BUSINESS_MODEL_LABELS,
  PLAN_ACTION_LABELS,
  TEMPLATE_SECTIONS,
  TEMPLATE_SECTION_LABELS,
  configurationTemplatesApi,
  type ApplyReport,
  type BusinessModel,
  type ConfigurationTemplateSummary,
  type PlanAction,
  type TemplatePreview,
  type TemplateStatus,
} from '../api/configurationTemplates'
import {
  Page,
  PageHeader,
  Button,
  IconButton,
  Callout,
  LoadingState,
  EmptyState,
  Card,
  CardHeader,
  CardContent,
  DataTable,
  StatusPill,
  Dialog,
  TextField,
  Select,
  type PillTone,
  useConfirm,
  FormGrid,
} from '../components/ui'

/**
 * F-081 (TASK-0335, REQ-256-001/002): template di configurazione (modello di business). L'amministratore
 * salva la configurazione corrente come template, lo pubblica e lo applica a un ambiente DOPO aver visto
 * l'anteprima, che divide ogni elemento in quattro classi: da creare, già identico, in conflitto (stesso
 * codice o nome ma contenuto diverso: non si tocca) e riferimento mancante (settore, operatore o prodotto
 * per nome che qui non esiste). L'applicazione crea solo ciò che manca, sempre in Bozza o inattivo; non
 * sovrascrive e non cancella mai. Non contiene ruoli, dashboard, report né task, e nessun importo.
 * Nessun template è precaricato. Pagina raggiungibile solo dall'Admin (il server risponde 403 agli altri).
 */
const ROME = 'Europe/Rome'

const STATUS_TONES: Record<TemplateStatus, PillTone> = {
  Bozza: 'neutral',
  Pubblicato: 'success',
  Archiviato: 'neutral',
}

const PLAN_ACTION_TONES: Record<PlanAction, PillTone> = {
  Crea: 'success',
  Identico: 'neutral',
  Conflitto: 'warning',
  RiferimentoMancante: 'error',
}

const MODELS = Object.keys(BUSINESS_MODEL_LABELS) as BusinessModel[]

function formatRome(iso: string): string {
  return new Date(iso).toLocaleString('it-IT', {
    timeZone: ROME,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

interface FormState {
  name: string
  businessModel: BusinessModel
  description: string
}

const EMPTY_FORM: FormState = { name: '', businessModel: 'AgenziaTelecomunicazioni', description: '' }

export function ConfigurationTemplatesPage() {
  const askConfirm = useConfirm()
  const [items, setItems] = useState<ConfigurationTemplateSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)

  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState<string | null>(null)

  const [preview, setPreview] = useState<TemplatePreview | null>(null)
  const [report, setReport] = useState<ApplyReport | null>(null)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      setItems((await configurationTemplatesApi.list()).items)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [])

  // F-096: dopo "Clona" la copia (Bozza v1) compare nell'elenco e se ne apre l'anteprima.
  const openAfterClone = useOpenAfterClone(items, t => { void openPreview(t) })

  async function run(id: number, action: () => Promise<unknown>, done?: string) {
    setBusyId(id)
    setError(null)
    setNotice(null)
    try {
      await action()
      if (done) setNotice(done)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Operazione non riuscita')
    } finally {
      setBusyId(null)
    }
  }

  function openSnapshot() {
    setForm(EMPTY_FORM)
    setModalError(null)
    setShowModal(true)
  }

  async function handleSnapshot() {
    if (!form.name.trim()) return setModalError('Il nome è obbligatorio')
    setSaving(true)
    setModalError(null)
    try {
      await configurationTemplatesApi.snapshot({
        name: form.name.trim(),
        businessModel: form.businessModel,
        description: form.description.trim() || null,
      })
      setShowModal(false)
      setNotice('Configurazione corrente salvata come template in Bozza.')
      await load()
    } catch (e) {
      setModalError(e instanceof Error ? e.message : 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  async function openPreview(t: ConfigurationTemplateSummary) {
    setBusyId(t.id)
    setError(null)
    setNotice(null)
    setReport(null)
    try {
      setPreview(await configurationTemplatesApi.preview(t.id))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Anteprima non disponibile')
    } finally {
      setBusyId(null)
    }
  }

  async function handleApply() {
    if (!preview) return
    if (!(await askConfirm({ title: 'Applica template', message: `Applicare "${preview.name}" v${preview.version}? Verranno creati ${preview.plan.totalCreate} elementi, ` +
      'tutti in Bozza o inattivi. Nulla di esistente viene modificato o cancellato.', confirmLabel: 'Applica' }))) return
    setBusyId(preview.templateId)
    setError(null)
    try {
      setReport(await configurationTemplatesApi.apply(preview.templateId))
      setPreview(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Applicazione non riuscita')
    } finally {
      setBusyId(null)
    }
  }

  if (loading) {
    return (
      <Page>
        <LoadingState />
      </Page>
    )
  }

  return (
    <Page>
      <PageHeader
        title="Template configurazione"
        icon="copy_all"
        back={{ to: '/settings', label: 'Impostazioni generali' }}
        actions={
          <Button variant="filled" icon="add" onClick={openSnapshot}>
            Salva configurazione corrente
          </Button>
        }
      />

      <Callout tone="info">Salva la configurazione corrente (pipeline e fasi, esiti chiamata, campi dinamici, automazioni, modelli documentali, regole di completezza) e riapplicala a un ambiente. L'applicazione crea solo ciò che manca, in Bozza o inattivo: non sovrascrive e non cancella mai. Ruoli, dashboard, report e task non fanno parte dei template.</Callout>

      {error && <Callout tone="error">{error}</Callout>}
      {notice && <Callout tone="success">{notice}</Callout>}

      <Card variant="outlined" padding="none">
        {items.length === 0 ? (
          <EmptyState
            icon="copy_all"
            title="Nessun template."
            description="Nessuno è precaricato: nasce dalla configurazione corrente."
          />
        ) : (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Modello di business</th>
                <th>Versione</th>
                <th>Stato</th>
                <th>Contenuto</th>
                <th>Creato</th>
                <th>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map(t => (
                <tr key={t.id}>
                  <td className="md-typescale-emphasized-body-medium">
                    {t.name}
                    {t.description && <div className="md-typescale-body-small text-muted">{t.description}</div>}
                  </td>
                  <td>{BUSINESS_MODEL_LABELS[t.businessModel] ?? t.businessModel}</td>
                  <td>v{t.version}</td>
                  <td><StatusPill tone={STATUS_TONES[t.status]}>{t.status}</StatusPill></td>
                  <td className="md-typescale-body-small">
                    {t.counts
                      ? TEMPLATE_SECTIONS.filter(s => t.counts![s] > 0)
                          .map(s => `${TEMPLATE_SECTION_LABELS[s]}: ${t.counts![s]}`).join(' · ') || 'vuoto'
                      : '—'}
                  </td>
                  <td className="md-typescale-body-small">{formatRome(t.createdAt)}</td>
                  <td>
                    <div className="d-flex flex-wrap gap-1">
                      <CloneConfigurationButton
                        entityLabel="template di configurazione"
                        url={`/api/configuration-templates/${t.id}/clone`}
                        defaultName={t.name}
                        note="La copia nasce in Bozza alla versione 1, con lo stesso contenuto."
                        onCloned={async id => { await load(); openAfterClone(id) }}
                      />
                      {t.status !== 'Archiviato' && (
                        <Button variant="outlined" size="sm" disabled={busyId === t.id}
                          onClick={() => void openPreview(t)}>
                          Anteprima / applica
                        </Button>
                      )}
                      {t.status === 'Bozza' && (
                        <Button variant="outlined" size="sm" disabled={busyId === t.id}
                          onClick={() => void run(t.id, () => configurationTemplatesApi.publish(t.id), 'Template pubblicato.')}>
                          Pubblica
                        </Button>
                      )}
                      {t.status !== 'Bozza' && (
                        <Button variant="outlined" size="sm" disabled={busyId === t.id}
                          onClick={() => void run(t.id, () => configurationTemplatesApi.newVersion(t.id), 'Nuova versione creata in Bozza.')}>
                          Nuova versione
                        </Button>
                      )}
                      {t.status === 'Pubblicato' && (
                        <Button variant="outlined" size="sm" disabled={busyId === t.id}
                          onClick={() => void run(t.id, () => configurationTemplatesApi.archive(t.id), 'Template archiviato.')}>
                          Archivia
                        </Button>
                      )}
                      {t.status === 'Bozza' && (
                        <Button variant="outlined" size="sm" disabled={busyId === t.id}
                          onClick={async () => {
                            if (await askConfirm({ title: 'Elimina bozza', message: `Eliminare la bozza "${t.name}" v${t.version}?`, confirmLabel: 'Elimina', destructive: true }))
                              void run(t.id, () => configurationTemplatesApi.remove(t.id), 'Bozza eliminata.')
                          }}>
                          Elimina
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>

      {preview && (
        <Card variant="outlined">
          <CardHeader
            title={`Anteprima di "${preview.name}" v${preview.version} — nulla è stato ancora scritto`}
            actions={<IconButton icon="close" size="sm" aria-label="Chiudi" onClick={() => setPreview(null)} />}
          />
          <CardContent>
            <div className="d-flex flex-column gap-3">
              {preview.plan.sections.map(s => (
                <div key={s.section}>
                  <div className="mb-1 md-typescale-emphasized-body-medium">
                    {TEMPLATE_SECTION_LABELS[s.section]}
                    <span className="text-muted md-typescale-body-small ms-2">
                      {s.create} da creare · {s.identical} identici · {s.conflicts} in conflitto · {s.missingReferences} con riferimento mancante
                    </span>
                  </div>
                  {s.items.length === 0 ? (
                    <div className="md-typescale-body-small text-muted">Nessun elemento.</div>
                  ) : (
                    <ul className="list-unstyled md-typescale-body-small mb-0">
                      {s.items.map(i => (
                        <li key={`${s.section}-${i.key}`}>
                          <StatusPill tone={PLAN_ACTION_TONES[i.action as PlanAction]} className="me-2">
                            {PLAN_ACTION_LABELS[i.action as PlanAction]}
                          </StatusPill>
                          <code>{i.key}</code>
                          {i.reason && <span className="text-muted ms-2">{i.reason}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
              <div>
                <Button variant="tonal" disabled={busyId === preview.templateId || preview.plan.totalCreate === 0}
                  onClick={() => void handleApply()}>
                  {preview.plan.totalCreate === 0
                    ? 'Niente da creare'
                    : `Applica: crea ${preview.plan.totalCreate} elementi in Bozza o inattivi`}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {report && (
        <Card variant="outlined">
          <CardHeader
            title={`Applicato "${report.name}" v${report.version}: ${report.created} elementi creati`}
            actions={<IconButton icon="close" size="sm" aria-label="Chiudi" onClick={() => setReport(null)} />}
          />
          <CardContent>
            <div className="d-flex flex-column gap-3">
              {report.moduleErrors.length > 0 && (
                <Callout tone="error">
                  {report.moduleErrors.map(m => <div key={m.module}>{m.module}: {m.error}</div>)}
                </Callout>
              )}
              <DataTable variant="plain">
                <thead>
                  <tr>
                    <th>Sezione</th>
                    <th>Creati</th>
                    <th>Identici</th>
                    <th>Conflitti</th>
                    <th>Rif. mancanti</th>
                    <th>Non riusciti</th>
                  </tr>
                </thead>
                <tbody>
                  {report.sections.map(s => (
                    <tr key={s.section}>
                      <td>{TEMPLATE_SECTION_LABELS[s.section]}</td>
                      <td className="md-num">{s.created}</td>
                      <td className="md-num">{s.identical}</td>
                      <td className="md-num">{s.conflicts}</td>
                      <td className="md-num">{s.missingReferences}</td>
                      <td className="md-num">{s.failed}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
              <p className="md-typescale-body-small text-muted mb-0">
                Quanto creato è in Bozza o inattivo: va rivisto e attivato a mano dalle rispettive pagine.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog as="form"
 onSubmit={(e) => { e.preventDefault(); void handleSnapshot() }}
        open={showModal}
        onClose={() => setShowModal(false)}
        dismissible={!saving}
        title="Salva la configurazione corrente"
        supportingText="Il template nasce in Bozza con tutta la configurazione attuale. Settori, operatori e prodotti sono salvati per nome, così il template si applica anche dove gli id sono diversi."
        actions={
          <>
            <Button variant="text" onClick={() => setShowModal(false)} disabled={saving}>
              Annulla
            </Button>
            <Button type="submit" variant="text" disabled={saving}>
              {saving ? 'Salvataggio…' : 'Salva'}
            </Button>
          </>
        }
      >
        <>
          {modalError && <Callout tone="error">{modalError}</Callout>}
          <FormGrid columns={1}>
            <TextField id="ct-name" label="Nome" type="text" value={form.name} maxLength={100}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            <Select id="ct-model" label="Modello di business" value={form.businessModel}
              onChange={e => setForm(f => ({ ...f, businessModel: e.target.value as BusinessModel }))}>
              {MODELS.map(m => <option key={m} value={m}>{BUSINESS_MODEL_LABELS[m]}</option>)}
            </Select>
            <TextField id="ct-desc" label="Descrizione" multiline rows={2} value={form.description} maxLength={1000}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </FormGrid>
        </>
      </Dialog>
    </Page>
  )
}
